import React, { useState } from "react";
import { FileText, ExternalLink, CheckCircle2, Clock, AlertCircle } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { endpoints } from "../lib/api";
import { toast } from "sonner";

const STATUS_META = {
  signed:  { label: "Signed",  icon: CheckCircle2, cls: "bg-emerald-100 text-emerald-700 border-emerald-200 dark:bg-emerald-950/30 dark:text-emerald-300 dark:border-emerald-900/50" },
  pending: { label: "Pending", icon: Clock,        cls: "bg-amber-100 text-amber-700 border-amber-200 dark:bg-amber-950/30 dark:text-amber-300 dark:border-amber-900/50" },
  expired: { label: "Expired", icon: AlertCircle,  cls: "bg-rose-100 text-rose-700 border-rose-200 dark:bg-rose-950/30 dark:text-rose-300 dark:border-rose-900/50" },
};

/**
 * Lightweight waiver/liability section on member profile.
 * Doesn't build signature infrastructure - just tracks status + link.
 *
 * defaultWaiverUrl: comes from gym settings (e.g. Google Form / Jotform link).
 */
export default function WaiverSection({ member, defaultWaiverUrl, onChanged }) {
  const status = member.waiver_status || "pending";
  const meta = STATUS_META[status] || STATUS_META.pending;
  const Icon = meta.icon;
  const url = member.waiver_url || defaultWaiverUrl || "";

  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [waiverUrl, setWaiverUrl] = useState(member.waiver_url || "");
  const [waiverStatus, setWaiverStatus] = useState(status);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await endpoints.setWaiver(member.id, {
        waiver_status: waiverStatus,
        waiver_url: waiverUrl || null,
      });
      toast.success("Waiver updated");
      onChanged?.();
      setOpen(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update waiver");
    } finally { setBusy(false); }
  };

  const markSigned = async () => {
    setBusy(true);
    try {
      await endpoints.setWaiver(member.id, {
        waiver_status: "signed",
        waiver_url: member.waiver_url || defaultWaiverUrl || null,
      });
      toast.success("Waiver marked signed");
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update waiver");
    } finally { setBusy(false); }
  };

  return (
    <section data-testid="waiver-section" className="bg-card border border-border rounded-xl p-5">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <FileText className="w-4 h-4 text-muted-foreground" />
          <h3 className="font-display text-base font-semibold text-foreground">Waiver</h3>
          <span data-testid={`waiver-status-${status}`} className={`inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-md border ${meta.cls}`}>
            <Icon className="w-2.5 h-2.5" /> {meta.label}
          </span>
        </div>
        <button
          onClick={() => setOpen(true)}
          data-testid="open-waiver-edit"
          className="text-xs font-medium px-2.5 py-1.5 rounded-md border border-border bg-card hover:bg-muted text-muted-foreground"
        >
          Edit
        </button>
      </div>

      <p className="text-xs text-muted-foreground/85 leading-relaxed mb-3">
        {status === "signed" && member.waiver_signed_at
          ? <>Signed on <span className="font-medium text-foreground">{new Date(member.waiver_signed_at).toLocaleDateString()}</span></>
          : status === "expired"
          ? <>Waiver has expired. Send the form again to re-confirm liability terms.</>
          : <>Combat sports liability / parent consent / medical declaration. Send the form via WhatsApp and mark signed once received.</>}
      </p>

      <div className="flex flex-wrap gap-2">
        {url && (
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="waiver-open-form"
            className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1.5 rounded-md bg-primary/10 text-primary hover:bg-primary/15"
          >
            <ExternalLink className="w-3 h-3" /> Open form
          </a>
        )}
        {status !== "signed" && (
          <button
            onClick={markSigned}
            disabled={busy}
            data-testid="mark-waiver-signed"
            className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1.5 rounded-md bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-60"
          >
            <CheckCircle2 className="w-3 h-3" /> Mark signed
          </button>
        )}
      </div>

      <Dialog open={open} onOpenChange={(o) => !o && setOpen(false)}>
        <DialogContent data-testid="waiver-edit-dialog" className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-display">Update waiver</DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-3 mt-2">
            <div>
              <Label className="text-xs">Status</Label>
              <div className="grid grid-cols-3 gap-2">
                {["pending", "signed", "expired"].map(s => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => setWaiverStatus(s)}
                    data-testid={`waiver-status-btn-${s}`}
                    className={`px-3 py-2 text-xs rounded-md border font-semibold capitalize ${
                      waiverStatus === s ? STATUS_META[s].cls : "border-border bg-card hover:bg-muted text-muted-foreground"
                    }`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <Label className="text-xs">Form / signed-document URL</Label>
              <Input
                type="url"
                value={waiverUrl}
                onChange={(e) => setWaiverUrl(e.target.value)}
                placeholder="https://forms.google.com/... or PDF link"
                data-testid="waiver-url-input"
              />
              <p className="text-[10px] text-muted-foreground mt-1">
                Paste your Google Form, Jotform or signed-PDF link. Members tap to open.
              </p>
            </div>
            <DialogFooter className="gap-2">
              <button type="button" onClick={() => setOpen(false)} disabled={busy} className="px-4 py-2 text-sm rounded-md border border-border bg-card hover:bg-muted">Cancel</button>
              <button type="submit" disabled={busy} data-testid="waiver-submit"
                className="px-4 py-2 text-sm rounded-md bg-primary text-primary-foreground font-medium hover:opacity-90">
                Save
              </button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </section>
  );
}
